import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { toArray, mergeMap, take, tap, map, filter } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { QueryBuilderConfig } from 'ngx-angular-query-builder';

import { ApplicationState } from '@store/application-store';
import * as cashBalancesActions from '@cash-balances/actions/cash-balances.actions';
import * as cashBalancesSelectors from '@cash-balances/selectors/cash-balance-selectors';
import { WidgetConfiguration, WidgetConfigurationsService } from '@feature/widget-configs';
import { WidgetAdminHandler, isWidgetType } from '@widget-admin/services/widget-admin-handler';
import { getDefaultWidgetConfigAdminForm, addFilterFieldsToAdminForm, addColumnFieldsToAdminForm } from '@feature/widget-admin/services/widget-admin-form';
import { SelectItem } from 'primeng/api';
import { CashBalancesViewType } from '../models/cash-balances.model';
import { CashBalanceConfiguration, SaveCashBalanceWidgetConfigurationRequest } from '../models/cash-balance-configuration.model';
import { CashBalanceGroupTypeProvider } from '../services/cash-balance-group-type-provider.service';
import { CashBalanceHttpService } from '../services/cash-balance-http.service';
import { Currency, CurrencyService } from '@currency/old_index';
import { mapWherePropertiesMatch } from '@common/index';
import { FormDefinition, FormFieldDefinition } from '@feature/_app_modules/dynamic-forms/dynamic-form';

@Injectable({
  providedIn
    : 'root'
})
export class CashBalancesWidgetAdminHandler implements WidgetAdminHandler {

  private cashBalanceConfigurations: CashBalanceConfiguration[];
  private defaultCurrencyCode: string;
  private currencyOptions: SelectItem[];
  private queryBuilderConfig: QueryBuilderConfig;

  constructor(
    private readonly store: Store<ApplicationState>,
    private readonly widgetConfigService: WidgetConfigurationsService,
    private readonly currencyService: CurrencyService,
    private readonly cashBalanceGroupTypeProvider: CashBalanceGroupTypeProvider,
    private readonly cashBalancesHttpService: CashBalanceHttpService) {
  }

  private getCashBalanceConfiguration(widgetConfigurationId: number): CashBalanceConfiguration {
    const match = this.cashBalanceConfigurations.find(x => x.WidgetConfigurationId === widgetConfigurationId);

    const defaultItem = () => {
      const item = new CashBalanceConfiguration();
      item.GroupByField1 = 'SubLedger';
      item.GroupByField2 = 'Type';
      item.View = 'cashBalanceTable';
      item.Currency = 'GBP';
      return item;
    };

    return match ? match : defaultItem();

  }

  getAdminForm(selectedConfig: WidgetConfiguration): FormDefinition {

    let formDefinition = getDefaultWidgetConfigAdminForm(selectedConfig);
    formDefinition = addFilterFieldsToAdminForm(selectedConfig, this.queryBuilderConfig, formDefinition);

    const cashBalancesConfig = this.getCashBalanceConfiguration(selectedConfig.id);

    if (!isWidgetType('CashBalances', selectedConfig)) {
      return formDefinition;
    }

    const views: SelectItem[] = [new CashBalancesViewOptions('cashBalanceTable', 'Table'), new CashBalancesViewOptions('bar', 'Stacked Column')];

    const foundView = views.find(x => {
      if (cashBalancesConfig) {
        return x.value === cashBalancesConfig.View;
      }
      return false;
    });
    const selectedView = foundView ? foundView : views[0];

    formDefinition.fields.push(
      {
        key: 'view',
        label: 'Default Chart View',
        value: selectedView,
        type: 'dropdown',
        dataKey: 'value',
        optionLabel: 'label',
        options: views,
        validation: [],
        asyncValidation: [],
      } as FormFieldDefinition
    );

    const chartGroupTypes = this.cashBalanceGroupTypeProvider.getChartGroupTypes();

    const findGroupType = chartGroupTypes.find(
      chartGroupType => {
        if (cashBalancesConfig) {
          return chartGroupType.GroupByField1 === cashBalancesConfig.GroupByField1 &&
            chartGroupType.GroupByField2 === cashBalancesConfig.GroupByField2;
        }
        return false;
      });

    const selectedChartGroupType = findGroupType ? findGroupType : chartGroupTypes[0];

    formDefinition.fields.push(
      {
        key: 'chartGroupType',
        label: `Default 'Balance By'`,
        value: selectedChartGroupType,
        type: 'dropdown',
        dataKey: 'Id',
        optionLabel: 'Label',
        options: chartGroupTypes,
        validation: [],
        asyncValidation: [],
      } as FormFieldDefinition
    );

    const currencyCodeDropDown = {
      key: 'currency',
      dataKey: 'value',
      optionLabel: 'label',
      label: 'Currency Code',
      value: cashBalancesConfig ?
        { value: cashBalancesConfig.Currency } :
        { value: this.defaultCurrencyCode },
      type: 'dropdown',
      disabled: false,
      options: this.currencyOptions,
      validation: [],
      asyncValidation: [],
    } as FormFieldDefinition;
    formDefinition.fields.push(currencyCodeDropDown);

    return formDefinition;
  }

  private createSaveRequest(selectedConfig: WidgetConfiguration, adminFormValue: any): SaveCashBalanceWidgetConfigurationRequest {

    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);

    const cashBalancesConfigSaveRequest = {} as SaveCashBalanceWidgetConfigurationRequest;

    cashBalancesConfigSaveRequest.widgetConfiguration = copyOfSelectedConfig;
    cashBalancesConfigSaveRequest.cashBalanceConfiguration = {
      WidgetConfigurationId: copyOfSelectedConfig.id,
      View: adminFormValue.view.value,
      GroupByField1: adminFormValue.chartGroupType.GroupByField1,
      GroupByField2: adminFormValue.chartGroupType.GroupByField2,
      Currency: adminFormValue.currency.value
    };

    return cashBalancesConfigSaveRequest;
  }

  createCopyFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {

    const saveRequest = this.createSaveRequest(selectedConfig, adminFormValue);

    return this.cashBalancesHttpService.createCopyOfCashBalanceConfiguration(saveRequest).pipe(map(s => true));

  }

  initialise(): Observable<boolean> {

    this.store.dispatch(new cashBalancesActions.LoadSubLedgers());

    const getCashBalanceConfigs = this.cashBalancesHttpService.getAllCashBalanceConfigurations().pipe(
      tap(configs => this.cashBalanceConfigurations = configs),
      map(() => true),
      take(1)
    );

    const getDefaultCurrency = this.currencyService.getDefaultCurrency().pipe(
      take(1),
      tap(currency => this.defaultCurrencyCode = currency.Code)
    );

    const getAllCurrencies = this.currencyService.getCurrencies().pipe(
      take(1),
      mergeMap((items: Currency[]) => items),
      map((c: Currency) => {
        return {
          value: c.Code,
          label: c.Code
        } as SelectItem;
      }),
      toArray(),
      tap(currencyOptions => this.currencyOptions = currencyOptions)
    );

    const getCashBalancesFilterConfig = this.store.pipe(
      select(cashBalancesSelectors.getSubLedgersQueryConfig()),
      filter(queryBuilderConfig => queryBuilderConfig != null),
      tap(queryBuilderConfig => this.queryBuilderConfig = queryBuilderConfig),
      take(1)
    );

    const all = forkJoin([
      getCashBalanceConfigs,
      getDefaultCurrency,
      getAllCurrencies,
      getCashBalancesFilterConfig]
    ).pipe(map(() => true));

    return all;

  }

  saveConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {

    const saveRequest = this.createSaveRequest(selectedConfig, adminFormValue);

    return this.cashBalancesHttpService.saveCashBalanceConfiguration(saveRequest).pipe(map(s => true));

  }

  deleteConfigFromAdminForm(selectedConfig: WidgetConfiguration, adminFormValue: any): Observable<boolean> {
    const copyOfSelectedConfig: WidgetConfiguration = mapWherePropertiesMatch(selectedConfig, adminFormValue);
    return this.widgetConfigService.delete(copyOfSelectedConfig).pipe(map(() => true));
  }
}

class CashBalancesViewOptions implements SelectItem {
  constructor(public value: CashBalancesViewType, public label: string) { }
}
